/**
 * Turning decoded audio into the samples the recogniser reads. Pure and
 * Angular-free so the arithmetic can be checked directly.
 *
 * The Speech SDK reads 16-bit signed samples, one channel, little-endian. Browsers
 * decode to floating point at whatever rate the recording was made in, so the two
 * do not meet without this step.
 */

/** What the SDK's default input stream format expects. */
export const RECOGNISER_SAMPLE_RATE = 16000;

/**
 * Mix the channels down to one and convert to 16-bit samples.
 *
 * A recording made in stereo carries the same voice in both channels; averaging
 * keeps it at the level it was spoken rather than doubling it into distortion.
 * Values outside the range are clamped, because a sample that wraps turns a loud
 * word into a burst of noise the recogniser reads as speech.
 */
export function toPcm16(channels: Float32Array[]): Int16Array {
  const kept = (channels ?? []).filter((channel) => channel?.length);
  if (!kept.length) {
    return new Int16Array(0);
  }
  const frames = kept[0].length;
  const out = new Int16Array(frames);
  for (let frame = 0; frame < frames; frame++) {
    let sum = 0;
    for (const channel of kept) {
      sum += channel[frame] ?? 0;
    }
    const mixed = sum / kept.length;
    const clamped = mixed > 1 ? 1 : mixed < -1 ? -1 : mixed;
    // Negative values reach one step further than positive ones, which is why the
    // two directions are scaled differently rather than by a single factor.
    out[frame] = Math.round(clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff);
  }
  return out;
}

/** The samples as the bytes the stream is fed. */
export function pcm16ToBytes(samples: Int16Array): ArrayBuffer {
  return samples.buffer.slice(samples.byteOffset, samples.byteOffset + samples.byteLength);
}

/** The length of a canonical WAV header: RIFF, `fmt ` for 16-bit PCM, and the `data` chunk header. */
const WAV_HEADER_BYTES = 44;

/**
 * The samples as a WAV file: 16-bit PCM, one channel, at `sampleRate`. A decoder
 * reads it by itself, which is what lets it sit in a recording next to files in
 * another format.
 */
export function toWav(samples: Int16Array, sampleRate: number = RECOGNISER_SAMPLE_RATE): Blob {
  const header = new DataView(new ArrayBuffer(WAV_HEADER_BYTES));
  const writeTag = (offset: number, tag: string): void => {
    for (let i = 0; i < tag.length; i++) {
      header.setUint8(offset + i, tag.charCodeAt(i));
    }
  };
  const dataBytes = samples.byteLength;
  writeTag(0, 'RIFF');
  header.setUint32(4, WAV_HEADER_BYTES - 8 + dataBytes, true);
  writeTag(8, 'WAVE');
  writeTag(12, 'fmt ');
  header.setUint32(16, 16, true);
  header.setUint16(20, 1, true);
  header.setUint16(22, 1, true);
  header.setUint32(24, sampleRate, true);
  header.setUint32(28, sampleRate * 2, true);
  header.setUint16(32, 2, true);
  header.setUint16(34, 16, true);
  writeTag(36, 'data');
  header.setUint32(40, dataBytes, true);
  return new Blob([header.buffer, pcm16ToBytes(samples)], { type: 'audio/wav' });
}

/**
 * Taps of the low-pass filter applied before the rate comes down. Odd, so the
 * filter is centred on a sample; with a Blackman window this many taps give a
 * transition band of about a twentieth of the input rate.
 */
const LOW_PASS_TAPS = 127;

/**
 * The low-pass cutoff as a share of the target rate's Nyquist frequency, so the
 * transition band sits below the frequency that would fold back into speech.
 */
const LOW_PASS_SHARE = 0.9;

/** A windowed-sinc low-pass filter with its cutoff at `cutoff` cycles per input sample, normalized to unity gain. */
function lowPassFilter(cutoff: number): Float64Array {
  const taps = new Float64Array(LOW_PASS_TAPS);
  const middle = (LOW_PASS_TAPS - 1) / 2;
  let sum = 0;
  for (let k = 0; k < LOW_PASS_TAPS; k++) {
    const n = k - middle;
    const sinc = n === 0 ? 2 * cutoff : Math.sin(2 * Math.PI * cutoff * n) / (Math.PI * n);
    const phase = (2 * Math.PI * k) / (LOW_PASS_TAPS - 1);
    taps[k] = sinc * (0.42 - 0.5 * Math.cos(phase) + 0.08 * Math.cos(2 * phase));
    sum += taps[k];
  }
  return taps.map((tap) => tap / sum);
}

/**
 * Brings audio captured at the platform's rate to the recogniser's, one batch at a
 * time. Coming down, the input is low-pass filtered below the target's Nyquist
 * frequency first, so what lies above it does not fold back into the speech band;
 * each output sample is then read off the filtered input by linear interpolation.
 * Positions are counted in whole samples from the first batch, so batches joined
 * make exactly the signal one batch would have, with no seam where they meet.
 */
export class Resampler {
  readonly #from: number;
  readonly #to: number;
  readonly #filter: Float64Array;
  readonly #reach: number;
  #held = new Float32Array(0);
  #heldStart = 0;
  #produced = 0;

  constructor(fromRate: number, toRate: number = RECOGNISER_SAMPLE_RATE) {
    this.#from = Math.round(fromRate);
    this.#to = Math.round(toRate);
    this.#filter = this.#from > this.#to ? lowPassFilter((LOW_PASS_SHARE * this.#to) / 2 / this.#from) : Float64Array.of(1);
    this.#reach = (this.#filter.length - 1) / 2;
  }

  /** The samples `input` completes, at the target rate. */
  public push(input: Float32Array): Float32Array {
    if (this.#from === this.#to) {
      return Float32Array.from(input);
    }
    const data = new Float32Array(this.#held.length + input.length);
    data.set(this.#held);
    data.set(input, this.#held.length);
    const end = this.#heldStart + data.length;
    const out: number[] = [];
    for (;;) {
      const scaled = this.#produced * this.#from;
      const index = Math.floor(scaled / this.#to);
      if (index + 1 + this.#reach >= end) {
        break;
      }
      const fraction = (scaled - index * this.#to) / this.#to;
      const before = this.#filtered(data, index);
      out.push(before + (this.#filtered(data, index + 1) - before) * fraction);
      this.#produced++;
    }
    const keepFrom = Math.max(this.#heldStart, Math.floor((this.#produced * this.#from) / this.#to) - this.#reach);
    this.#held = data.slice(keepFrom - this.#heldStart);
    this.#heldStart = keepFrom;
    return Float32Array.from(out);
  }

  /** The filtered input at absolute index `at`; input before the first sample counts as silence. */
  #filtered(data: Float32Array, at: number): number {
    let sum = 0;
    for (let k = 0; k < this.#filter.length; k++) {
      const index = at - this.#reach + k;
      if (index >= 0) {
        sum += this.#filter[k] * data[index - this.#heldStart];
      }
    }
    return sum;
  }
}
